import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import { cn } from '../../lib/utils';

const BillSummary = ({ totals, backendTotals = null, discountPercent = 0, shippingFee = 0, paidAmount = 0, advanceAmount = 0, isAdvanceMerged = false, totalItemsCount = null }) => {
    const effectiveSubtotal = Number(backendTotals?.subtotal ?? totals?.subtotal ?? 0);
    const effectiveTotalDiscount = Number(backendTotals?.totalDiscount ?? totals?.totalDiscount ?? 0);
    const effectiveTaxableValue = Number(backendTotals?.taxableValue ?? totals?.taxableValue ?? 0);
    const effectiveTotalTax = Number(backendTotals?.totalTax ?? totals?.totalTax ?? 0);
    const effectiveShippingFee = Number(backendTotals?.shippingFee ?? shippingFee ?? 0);
    const effectiveGrandTotal = Number(backendTotals?.grandTotal ?? totals?.grandTotal ?? 0);
    const effectiveTotalQuantity = Number(
        totalItemsCount ?? backendTotals?.totalQuantity ?? totals?.totalQuantity ?? 0
    );

    const safePaidAmount = Number(paidAmount) || 0;
    const safeAdvanceAmount = Number(advanceAmount) || 0;
    const totalCovered = isAdvanceMerged ? safePaidAmount : (safePaidAmount + safeAdvanceAmount);
    const effectiveBalanceDue = effectiveGrandTotal - totalCovered;
    const normalizeMoney = (value) => {
        const n = Number(value || 0);
        if (!Number.isFinite(n)) return 0;
        return Object.is(n, -0) ? 0 : n;
    };
    const formatInr = (value) => `₹${normalizeMoney(value).toFixed(2)}`;
    const paidProgressPercent = effectiveGrandTotal > 0
        ? Math.max(0, Math.min(100, (safePaidAmount / effectiveGrandTotal) * 100))
        : 0;
    const hasNonZeroDiscount = normalizeMoney(effectiveTotalDiscount) > 0;
    const hasNonZeroShipping = normalizeMoney(effectiveShippingFee) > 0;
    const hasNonZeroTax = normalizeMoney(effectiveTotalTax) > 0;

    const dueAmount = Math.max(0, normalizeMoney(effectiveBalanceDue));
    const isPaidInFull = dueAmount <= 0;
    const statusLabel = isPaidInFull ? '🟢 Paid' : '🔴 Pending';

    return (
        <Card className="border-slate-200/80 shadow-sm">
            <CardHeader className="pb-3">
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex justify-between items-center">
                    Billing Summary
                    {backendTotals && (
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-bold">
                            ✔ Server Verified
                        </span>
                    )}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">🛒 Order Overview</p>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Items</span>
                        <span className="font-semibold">{effectiveTotalQuantity}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span>{formatInr(effectiveSubtotal)}</span>
                    </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">💸 Charges & Adjustments</p>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Taxable Amount</span>
                        <span>{formatInr(effectiveTaxableValue)}</span>
                    </div>
                    {hasNonZeroShipping && (
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Shipping Fee</span>
                            <span>{formatInr(effectiveShippingFee)}</span>
                        </div>
                    )}
                    {hasNonZeroDiscount && (
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Discount</span>
                            <span>-{formatInr(effectiveTotalDiscount)}</span>
                        </div>
                    )}
                    {hasNonZeroTax && (
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">GST</span>
                            <span>+{formatInr(effectiveTotalTax)}</span>
                        </div>
                    )}
                </div>

                <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 space-y-2">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">💰 Payment Summary</p>
                    <Separator />
                    <div className="flex justify-between text-lg font-extrabold">
                        <span>Grand Total</span>
                        <span>{formatInr(effectiveGrandTotal)}</span>
                    </div>
                    <Separator />
                    <div className="space-y-1 pt-1">
                        <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                            <div
                                className="h-3 bg-emerald-500 rounded-full transition-all duration-500"
                                style={{ width: `${paidProgressPercent}%` }}
                            />
                        </div>
                        <p className="text-[11px] text-slate-500">
                            {formatInr(safePaidAmount)} of {formatInr(effectiveGrandTotal)} paid • {formatInr(dueAmount)} remaining
                        </p>
                    </div>
                    <div className="flex justify-between items-center pt-1">
                        <span className={cn(
                            "text-[10px] px-2 py-1 rounded-full font-bold tracking-wider uppercase",
                            isPaidInFull ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                        )}>
                            {statusLabel}
                        </span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">🟢 Paid</span>
                        <span className="font-semibold text-emerald-600">{formatInr(safePaidAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">🔴 Due</span>
                        <span className={cn("font-extrabold text-base", effectiveBalanceDue > 0 ? "text-destructive" : "text-emerald-600")}>
                            {formatInr(effectiveBalanceDue)}
                        </span>
                    </div>
                    <Separator />
                    <div className={cn(
                        "text-sm font-semibold text-center py-2 rounded-lg",
                        isPaidInFull ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                    )}>
                        {isPaidInFull ? "Paid in Full ✅" : `Pending payment: ${formatInr(dueAmount)}`}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default React.memo(BillSummary);